<template>
  <v-menu
    v-model="menuOpen"
    bottom
    left
    offset-y
    max-width="320"
    content-class="portal-account-menu-content"
  >
    <template v-slot:activator="{ on, attrs }">
      <slot name="activator" :on="on" :attrs="attrs" />
    </template>

    <v-list class="portal-account-menu-list">
      <!-- 1. Which studio you are looking at. Not the email: the portal is studio-scoped, and the
           address the person logged in with answers nothing they need at this moment. -->
      <v-subheader class="portal-account-menu-current">{{ currentAccountName }}</v-subheader>

      <!-- 2. The switcher, only when there is somewhere to switch to. -->
      <template v-if="hasMultipleAccounts">
        <v-divider />
        <v-list-item
          v-for="account in allAccounts"
          :key="accountKey(account)"
          class="portal-account-menu-item"
          :class="{ 'portal-account-menu-item--active': isCurrentAccount(account) }"
          @click="switchAccount(account)"
        >
          <v-list-item-avatar size="36" color="var(--color-pallet-1)">
            <v-icon v-if="!getAccountLogo(account)" color="white" size="20">
              {{ getAccountIcon(account) }}
            </v-icon>
            <img v-else :src="getAccountLogo(account)" :alt="account.displayName" />
          </v-list-item-avatar>

          <v-list-item-content>
            <v-list-item-title>{{ account.displayName }}</v-list-item-title>
            <v-list-item-subtitle v-if="account.organizationId">
              {{ getRolesText(account.roles) }}
            </v-list-item-subtitle>
            <v-list-item-subtitle v-else>
              {{ $t('layout.account-switcher.participant-account') }}
            </v-list-item-subtitle>
          </v-list-item-content>

          <v-list-item-action v-if="isCurrentAccount(account)">
            <v-icon color="var(--color-pallet-1)">mdi-check</v-icon>
          </v-list-item-action>
        </v-list-item>
      </template>

      <v-divider />

      <!-- 3. The person, not the studio: name, email, password. Org-independent path, so it still
           resolves for a login with no studio account. -->
      <v-list-item :to="userProfilePath" class="portal-account-menu-item">
        <v-list-item-icon class="mr-2">
          <v-icon color="white" size="20">mdi-account-cog</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>{{ $t('layout.mypages.navigation.my-account') }}</v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <!-- 4. Also at the bottom of the drawer, deliberately. People look for logout at the bottom,
           and they look for it under their own name - it belongs in both places. -->
      <v-list-item class="portal-account-menu-item portal-account-menu-logout" @click="logout">
        <v-list-item-icon class="mr-2">
          <v-icon color="var(--color-red-1)" size="20">mdi-logout</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>{{ $t('layout.menu.logout') }}</v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script>
import urlUtil from '@/js/utils/urlUtil'
import { USER_PROFILE_PATH } from '@/router/myPagesPaths'

/**
 * The account menu behind the portal drawer header: which studio, switch studio, my profile,
 * log out.
 *
 * Why it exists: `Mans profils` used to sit at the bottom of the drawer next to `Iziet`, directly
 * under `Uzstadijumi`, so nothing said whether it held the person's login or the studio's account.
 * Putting it under the person's own name answers that without a rename. See
 * `docs/features/settings/ia-decision.md` -> *Portal account menu (2026-09-23)*.
 *
 * Deliberately separate from `UserAccountMenu`, which also serves the public website, the studio
 * pages and the portal's top-right menu - a change for this drawer would otherwise change all
 * four. It replaces `AccountSwitcher`, whose only job was the list below and whose only consumer
 * was this same header.
 *
 * The activator is the caller's: the drawer header (logo, studio name, user name, chevron) is
 * unchanged by this component and is passed in through the slot.
 */
export default {
  name: 'PortalAccountMenu',
  data () {
    return {
      menuOpen: false
    }
  },
  computed: {
    userProfilePath () {
      return urlUtil.getLocalisedUrl(USER_PROFILE_PATH)
    },
    allAccounts () {
      return this.$store.getters['user/accountsForSwitcher']
    },
    hasMultipleAccounts () {
      return this.allAccounts.length > 1
    },
    currentAccount () {
      return this.$store.getters['dashboard/currentAccount']
    },
    currentAccountName () {
      return this.currentAccount ? this.currentAccount.displayName : ''
    }
  },
  methods: {
    accountKey (account) {
      return account.organizationIdentifier
    },
    isCurrentAccount (account) {
      if (!this.currentAccount) return false
      return this.currentAccount.type === account.type &&
        this.currentAccount.organizationId === account.organizationId
    },
    getAccountIcon (account) {
      return account.organizationId ? 'mdi-domain' : 'mdi-account'
    },
    getAccountLogo (account) {
      if (!account) return null
      return account.logo_url || null
    },
    getRolesText (roles) {
      if (!roles || roles.length === 0) return ''
      return roles.map(role => this.$t(`layout.account-switcher.role.${role.toLowerCase()}`)).join(', ')
    },
    switchAccount (account) {
      if (account.account_url) {
        this.$router.push(account.account_url)
      }
      this.menuOpen = false
    },
    logout () {
      this.menuOpen = false
      this.$store.dispatch('user/logout')
    }
  }
}
</script>

<style scoped>
.portal-account-menu-list {
  max-height: 420px;
  overflow-y: auto;
}

.portal-account-menu-current {
  font-weight: 600;
  height: 36px;
}
</style>

<style>
/* Menu content is teleported to body, so it cannot inherit the drawer's theme - restate it here,
   the same way the drawer's own gradient is defined. */
.portal-account-menu-content {
  background: linear-gradient(180deg, var(--color-pallet-5) 0%, var(--color-pallet-6) 100%) !important;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.portal-account-menu-content .v-list {
  background: transparent !important;
}

.portal-account-menu-content .v-subheader {
  color: rgba(255, 255, 255, 0.9) !important;
}

.portal-account-menu-content .v-list-item__title,
.portal-account-menu-content .v-list-item__subtitle {
  color: rgba(255, 255, 255, 0.95) !important;
}

.portal-account-menu-content .v-list-item:hover {
  background: rgba(255, 255, 255, 0.1) !important;
}

.portal-account-menu-content .portal-account-menu-item--active {
  background: rgba(255, 255, 255, 0.12) !important;
}

.portal-account-menu-content .portal-account-menu-logout .v-list-item__title {
  color: var(--color-red-1) !important;
}
</style>
